// Google Ads tag for the platform app — configuration and reporting.
//
// Kept free of react-router imports so it can be unit tested (the repo's jest
// setup cannot resolve react-router-dom; see App.test.tsx). The router-bound
// route tracker lives in googleAds.tsx.
//
// Conversion IDs and labels are public by design — they appear in the page
// source of every advertiser using gtag, and grant no account access. They are
// hardcoded like the PostHog key and Reddit pixel ID, since the production
// Docker build injects no env vars. Leave GOOGLE_ADS_ID empty to disable.
//
// Ads land on usekestrel.ai but signup happens here, on
// platform.usekestrel.ai. Both hosts must load the tag with the same linker
// domains or gtag treats the hop as a new session and the ad click never gets
// credited for the trial.
export const GOOGLE_ADS_ID = 'AW-18414388857';

/**
 * Conversion label for the "Trial signup" action (conversion type ID 7736589803),
 * from that action's event snippet in Google Ads.
 *
 * This is the *page load* variant of the snippet: the conversion fires when the
 * customer first reaches /setup, which happens exactly once per new account
 * right after registration succeeds. The click variant Google also offers would
 * mean firing on a CTA click instead — wrong here, since clicking "Start free
 * trial" on the marketing site is not a signup and most clicks never finish one.
 */
export const TRIAL_SIGNUP_SEND_TO: string = 'AW-18414388857/eVHjCOv7i-kcEPmM1cxE';

const LINKER_DOMAINS = ['usekestrel.ai', 'platform.usekestrel.ai'];

/**
 * Marks a trial signup as already reported. Session-scoped rather than a ref
 * because the customer lands on /setup once per signup but may revisit it (or
 * reload) while finishing onboarding, and none of those may count again.
 */
export const TRIAL_REPORTED_KEY = 'kestrel_trial_conversion_sent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Loads gtag.js and configures cross-domain linking. No-op when unconfigured. */
export function initGoogleAds() {
  if (!GOOGLE_ADS_ID || typeof window === 'undefined' || window.gtag) return;

  window.dataLayer = window.dataLayer || [];
  // gtag.js recognises a command only when the pushed item is a raw `arguments`
  // object; a normalized array is silently ignored, which drops `js`, `config`
  // and every conversion event. This mirrors Google's official snippet exactly:
  //   function gtag(){dataLayer.push(arguments);}
  const gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  } as unknown as NonNullable<Window['gtag']>;
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GOOGLE_ADS_ID, {
    linker: { domains: LINKER_DOMAINS, decorate_forms: true },
  });

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  script.async = true;
  document.head.appendChild(script);
}

/** Whether the trial conversion has already been reported this session. */
function trialAlreadyReported(): boolean {
  try {
    return window.sessionStorage.getItem(TRIAL_REPORTED_KEY) === '1';
  } catch {
    // Private browsing can throw on sessionStorage access; fall back to
    // reporting, since an occasional duplicate beats never converting.
    return false;
  }
}

function markTrialReported() {
  try {
    window.sessionStorage.setItem(TRIAL_REPORTED_KEY, '1');
  } catch {
    /* storage unavailable */
  }
}

/**
 * Reports the trial-signup conversion, at most once per session. Returns
 * whether a conversion was actually sent, which keeps this testable without a
 * live gtag.
 */
export function reportTrialSignupConversion(): boolean {
  if (!GOOGLE_ADS_ID || !TRIAL_SIGNUP_SEND_TO) return false;
  if (typeof window === 'undefined' || !window.gtag) return false;
  if (trialAlreadyReported()) return false;

  window.gtag('event', 'conversion', { send_to: TRIAL_SIGNUP_SEND_TO });
  markTrialReported();
  return true;
}
